import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a copy control stays in its "Copied" state. */
const COPY_FEEDBACK_MS = 2000;

/** What {@link useCopyToClipboard} hands back to a page. */
interface CopyToClipboard {
  /** Key of the control that was most recently copied, or null. */
  copiedKey: string | null;
  /** Message from the last failed copy, or null. */
  copyError: string | null;
  /** Copy `text`, flagging `key` as copied for a couple of seconds. */
  copy: (key: string, text: string) => void;
}

/**
 * Shared clipboard state for pages with several copy controls (a research
 * reference, a bare id, a public share link). Keying by control means one
 * timer and one piece of state drives every button's "Copied" label instead of
 * a boolean per button.
 *
 * Clipboard writes are asynchronous and can settle out of order, so each call
 * takes a sequence number and only the newest one is allowed to touch state or
 * arm the reset timer. A slow earlier write can no longer clobber the feedback
 * of the control the user clicked last, and a write that settles after unmount
 * is dropped entirely.
 */
export function useCopyToClipboard(): CopyToClipboard {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRequest = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const copy = useCallback((key: string, text: string) => {
    latestRequest.current += 1;
    const request = latestRequest.current;

    void (async () => {
      let succeeded: boolean;
      try {
        await navigator.clipboard.writeText(text);
        succeeded = true;
      } catch {
        succeeded = false;
      }

      // A newer copy started while this write was in flight — it owns the
      // feedback state and the timer now. An unmounted page owns nothing.
      if (!mounted.current || request !== latestRequest.current) return;

      if (resetTimer.current) clearTimeout(resetTimer.current);

      if (!succeeded) {
        setCopiedKey(null);
        setCopyError('could not copy to clipboard');
        return;
      }

      setCopyError(null);
      setCopiedKey(key);
      resetTimer.current = setTimeout(() => setCopiedKey(null), COPY_FEEDBACK_MS);
    })();
  }, []);

  return { copiedKey, copyError, copy };
}
